import React from 'react';
import {
  Paper,
  Text,
  Group,
  Title,
  Stack,
  Badge,
  Button,
  Progress,
  Box,
  Divider,
} from '@mantine/core';
import { createStyles } from '@mantine/styles';
import {
  IconArrowBack,
  IconMan,
  IconWoman,
  IconBrain,
  IconChartBar,
  IconAlertCircle,
  IconNetwork,
} from '@tabler/icons-react';

const useStyles = createStyles((theme) => ({
  resultCard: {
    width: '100%',
    maxWidth: '700px',
    background: 'var(--card-gradient)',
    borderRadius: 'var(--border-radius-lg)',
    boxShadow: 'var(--shadow-lg)',
    border: '1px solid rgba(255, 255, 255, 0.8)',
    overflow: 'hidden',
    margin: '0 auto',
    padding: '40px',
    transition: 'var(--transition)',
    animation: 'fadeIn 0.6s ease-out',
    backdropFilter: 'blur(10px)',
    '[data-theme="dark"] &': {
      border: '1px solid rgba(30, 41, 59, 0.8)',
    },
    '@media (max-width: 768px)': {
      padding: '24px',
    },
  },
  title: {
    color: 'var(--primary-color)',
    fontSize: '1.75rem',
    fontWeight: 700,
    marginBottom: '32px',
    width: '100%',
    textAlign: 'center',
    lineHeight: 1.2,
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: '-10px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '60px',
      height: '3px',
      background:
        'linear-gradient(90deg, var(--primary-color), var(--secondary-color))',
      borderRadius: '10px',
    },
  },
  photoContainer: {
    width: '180px',
    height: '180px',
    borderRadius: '50%',
    overflow: 'hidden',
    border: '4px solid white',
    boxShadow: 'var(--shadow-md)',
    margin: '0 auto 36px',
    position: 'relative',
    transition: 'var(--transition)',
    '&:hover': {
      transform: 'scale(1.05)',
      boxShadow: 'var(--shadow-lg)',
    },
    '@media (max-width: 576px)': {
      width: '150px',
      height: '150px',
    },
  },
  photo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  resultsSection: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    gap: '24px',
    marginBottom: '36px',
    '@media (max-width: 576px)': {
      flexDirection: 'column',
      alignItems: 'center',
      gap: '20px',
    },
  },
  resultItem: {
    flex: 1,
    padding: '24px',
    backgroundColor: 'white',
    borderRadius: 'var(--border-radius)',
    boxShadow: 'var(--shadow-sm)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'var(--transition)',
    border: '1px solid rgba(241, 245, 249, 0.9)',
    '[data-theme="dark"] &': {
      backgroundColor: 'var(--bg-card)',
      border: '1px solid rgba(30, 41, 59, 0.8)',
    },
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: 'var(--shadow-md)',
    },
  },
  resultIcon: {
    fontSize: '20px',
    color: 'var(--text-light)',
    marginBottom: '16px',
    padding: '10px',
    borderRadius: '50%',
    backgroundColor: 'var(--bg-light)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  resultLabel: {
    color: 'var(--text-secondary)',
    fontSize: '14px',
    marginBottom: '8px',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  ageValue: {
    fontSize: '48px',
    fontWeight: 800,
    lineHeight: 1.1,
    background:
      'linear-gradient(90deg, var(--primary-color), var(--secondary-color))',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    marginBottom: '4px',
  },
  ageUnit: {
    fontSize: '14px',
    color: 'var(--text-secondary)',
    fontWeight: 500,
  },
  genderContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '12px',
  },
  genderIcon: {
    marginRight: '10px',
  },
  genderValue: {
    fontSize: '28px',
    fontWeight: 700,
    color: 'var(--text-primary)',
  },
  confidenceBadge: {
    marginBottom: '10px',
  },
  backButton: {
    background:
      'linear-gradient(45deg, var(--primary-color), var(--secondary-color))',
    color: 'white',
    fontWeight: 600,
    border: 'none',
    height: '48px',
    padding: '0 32px',
    transition: 'var(--transition)',
    borderRadius: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:hover': {
      background:
        'linear-gradient(45deg, var(--primary-hover), var(--secondary-color))',
      transform: 'translateY(-2px)',
      boxShadow: 'var(--shadow-md)',
    },
  },
  progressBar: {
    width: '100%',
    height: '6px',
    marginTop: '8px',
  },
  confidenceText: {
    color: 'var(--text-secondary)',
    fontSize: '14px',
    marginTop: '8px',
    textAlign: 'center',
  },
  maleColor: {
    color: '#4cc9f0',
  },
  femaleColor: {
    color: '#f72585',
  },
  modelInfo: {
    marginTop: '20px',
    padding: '20px',
    background:
      'linear-gradient(135deg, rgba(67, 97, 238, 0.05), rgba(76, 201, 240, 0.1))',
    backdropFilter: 'blur(10px)',
    borderRadius: 'var(--border-radius-lg)',
    width: '100%',
    textAlign: 'center',
    border: '1px solid rgba(255, 255, 255, 0.7)',
    boxShadow: 'var(--shadow-sm)',
    position: 'relative',
    overflow: 'hidden',
  },
  modelInfoTitle: {
    fontSize: '15px',
    fontWeight: 700,
    color: 'var(--text-primary)',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    letterSpacing: '0.01em',
  },
  modelDetails: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modelBadge: {
    padding: '6px 14px',
    fontSize: '0.9rem',
    fontWeight: 600,
    borderRadius: 'var(--border-radius-full)',
    boxShadow: '0 2px 5px rgba(0,0,0,0.07)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
    transform: 'translateY(0)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
    },
  },
  modelIconContainer: {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    opacity: 0.05,
    overflow: 'hidden',
  },
  modelIcon: {
    position: 'absolute',
    bottom: '-20px',
    right: '-20px',
    color: 'var(--primary-color)',
    opacity: 0.8,
  },
  errorContainer: {
    padding: '32px',
    textAlign: 'center',
  },
  errorIcon: {
    color: '#e03131',
    marginBottom: '16px',
  },
  errorTitle: {
    color: '#e03131',
    fontSize: '1.25rem',
    fontWeight: 600,
    marginBottom: '12px',
  },
  errorMessage: {
    color: 'var(--text-secondary)',
    marginBottom: '24px',
  },
}));

const ResultDisplay = ({ image, predictions, onBack, modelInfo }) => {
  const { classes } = useStyles();

  // Check if there's an error in predictions
  if (predictions && predictions.error) {
    return (
      <Stack spacing="lg" align="center">
        <Paper className={classes.resultCard}>
          <div className={classes.errorContainer}>
            <IconAlertCircle size={48} className={classes.errorIcon} />
            <Title order={3} className={classes.errorTitle}>
              Analysis Failed
            </Title>
            <Text className={classes.errorMessage}>{predictions.error}</Text>
            <Button
              leftSection={<IconArrowBack size={18} />}
              onClick={onBack}
              className={classes.backButton}
            >
              Try Again
            </Button>
          </div>
        </Paper>
      </Stack>
    );
  }

  // This is a placeholder. The actual predictions would come from the ML model.
  const { age, gender, confidence } = predictions || {
    age: 28,
    gender: 'Male',
    confidence: 0.92,
  };

  const genderColor = gender === 'Male' ? 'blue' : 'pink';
  const GenderIcon = gender === 'Male' ? IconMan : IconWoman;
  const genderColorClass =
    gender === 'Male' ? classes.maleColor : classes.femaleColor;

  // Convert confidence to percentage
  const confidencePercentage = Math.round(confidence * 100);

  return (
    <Stack spacing="lg" align="center">
      <Paper className={classes.resultCard}>
        <Title order={3} className={classes.title}>
          Analysis Results
        </Title>

        <div className={classes.photoContainer}>
          <img src={image} alt="Analyzed face" className={classes.photo} />
        </div>

        <div className={classes.resultsSection}>
          <div className={classes.resultItem}>
            <div className={classes.resultIcon}>
              <IconBrain
                size={24}
                color={gender === 'Male' ? '#4cc9f0' : '#f72585'}
              />
            </div>
            <Text className={classes.resultLabel}>Predicted Age</Text>
            <Text className={classes.ageValue}>{age}</Text>
            <Text className={classes.ageUnit}>years old</Text>
          </div>

          <div className={classes.resultItem}>
            <div className={classes.resultIcon}>
              <GenderIcon size={24} className={genderColorClass} />
            </div>
            <Text className={classes.resultLabel}>Gender</Text>
            <div className={classes.genderContainer}>
              <Text className={classes.genderValue}>{gender}</Text>
            </div>
          </div>
        </div>

        {modelInfo && (
          <>
            <Divider
              style={{
                margin: '30px 0 25px',
                opacity: 0.5,
                borderTop: '1px solid var(--primary-color)',
                width: '60%',
                alignSelf: 'center',
              }}
            />
            <div className={classes.modelInfo}>
              <div className={classes.modelIconContainer}>
                <IconBrain
                  size={120}
                  className={classes.modelIcon}
                  stroke={1}
                />
              </div>
              <Text className={classes.modelInfoTitle}>
                <IconNetwork
                  size={18}
                  style={{ marginRight: '8px', color: 'var(--primary-color)' }}
                  stroke={2}
                />
                AI Model Information
              </Text>
              <div className={classes.modelDetails}>
                <Badge
                  className={classes.modelBadge}
                  color="blue"
                  styles={{
                    root: {
                      backgroundColor: 'rgba(67, 97, 238, 0.1)',
                      color: 'var(--primary-color)',
                    },
                  }}
                >
                  {modelInfo.name}
                </Badge>
                <Badge
                  className={classes.modelBadge}
                  color="teal"
                  styles={{
                    root: {
                      backgroundColor: 'rgba(46, 204, 113, 0.1)',
                      color: '#2ecc71',
                    },
                  }}
                >
                  {modelInfo.precision}
                </Badge>
                {modelInfo.features &&
                  modelInfo.features.map((feature, idx) => (
                    <Badge
                      key={idx}
                      className={classes.modelBadge}
                      color="gray"
                      styles={{
                        root: {
                          backgroundColor: 'rgba(100, 116, 139, 0.1)',
                          color: 'var(--text-secondary)',
                        },
                      }}
                    >
                      {feature}
                    </Badge>
                  ))}

                {modelInfo.size && (
                  <Badge
                    className={classes.modelBadge}
                    color="dark"
                    styles={{
                      root: {
                        backgroundColor: 'rgba(45, 55, 72, 0.1)',
                        color: 'var(--text-primary)',
                      },
                    }}
                  >
                    {modelInfo.size}
                  </Badge>
                )}
              </div>
            </div>
          </>
        )}

        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '24px',
          }}
        >
          <Button
            leftSection={<IconArrowBack size={18} />}
            onClick={onBack}
            className={classes.backButton}
          >
            Try Another Photo
          </Button>
        </div>
      </Paper>
    </Stack>
  );
};

export default ResultDisplay;
